/**
 * 10.9EO — TITA DO ECLIPSE, o boss da RAID.
 *
 * Gigante parado no centro da arena (~10m), com um eclipse no lugar do rosto e um halo que gira.
 * Nao anda: numa raid com dezenas de pessoas em volta, quem se mexe e o GRUPO.
 *
 * Duas regras que fazem ele funcionar com 1000 jogadores sem ninguem "hospedar":
 *  1) a VIDA e do servidor. O dano que voce causa entra aqui so como retorno visual e sai em
 *     pacote pro servidor; a barra mostra a vida GLOBAL que o servidor manda 1x/s. Ele nunca
 *     morre por conta propria — so quando o servidor diz que morreu.
 *  2) os GOLPES sao roteirizados: indice = (relogio do servidor - inicio) / periodo, e o tipo sai
 *     de um sorteio com a semente do horario. Todo celular ve o mesmo golpe na mesma hora, e cada
 *     um decide sozinho se foi atingido (a vida do jogador e dele).
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import type { Boss, BossDebug } from "../boss/Boss";
import type { HookReaction } from "../player/HookSystem";
import type { Player } from "../player/Player";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";

/** Vida espelhada do servidor: acumula o dano local (pra mandar) e nunca zera sozinha. */
class RaidHealth extends HealthComponent {
  pending = 0;
  ended = false;
  damage(amount: number): number {
    if (this.ended || amount <= 0) return 0;
    this.pending += amount;
    this.current = Math.max(1, this.current - amount);
    return amount;
  }
}

type Kind = "slam" | "nova" | "donut" | "meteors";
const KINDS: Kind[] = ["slam", "slam", "slam", "meteors", "meteors", "meteors", "nova", "nova", "donut", "donut"];

/** Sorteio deterministico (mulberry32) — mesma semente => mesma sequencia em todo celular. */
function rand(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Pending { kind: Kind; at: number; pts: { x: number; z: number; r: number }[]; pct: number; kb: number; donutMesh?: THREE.Mesh }

export interface RaidTitanDeps {
  player: Player;
  telegraph: TelegraphSystem;
  particles: ParticleSystem;
  impulse: (s: number) => void;
  serverNow: () => number;
}

const _v = new THREE.Vector3();

export class RaidTitan implements Boss {
  readonly group = new THREE.Group();
  readonly health: RaidHealth;
  readonly radius: number;
  readonly armor = 0;
  pulled = false;
  removeRequested = false;
  private dead = false;
  private deathT = 0;
  private flash = 0;
  private t = 0;
  private lastIdx = -1;
  private pending: Pending[] = [];
  private raise = 0; // 0..1 bracos erguidos (preparando golpe)
  private readonly cfg = GameConfig.raid.titan;
  private readonly arena = GameConfig.raid.arena;
  private readonly mats: THREE.MeshStandardMaterial[] = [];
  private readonly halo: THREE.Mesh;
  private readonly corona: THREE.Mesh;
  private readonly armL = new THREE.Group();
  private readonly armR = new THREE.Group();
  private readonly shards: THREE.Mesh[] = [];
  private readonly shield: THREE.Mesh;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly deps: RaidTitanDeps,
    private readonly startAt: number,
    private readonly seed: number,
    hpMax: number,
    hp: number,
  ) {
    this.radius = this.cfg.radius;
    this.health = new RaidHealth(Math.max(1, hpMax));
    this.health.current = Math.max(1, hp);
    this.group.position.set(this.arena.x, 0, this.arena.z);
    this.shield = this.build();
    scene.add(this.group);
    const halo = this.group.getObjectByName("halo") as THREE.Mesh;
    const corona = this.group.getObjectByName("corona") as THREE.Mesh;
    this.halo = halo;
    this.corona = corona;
  }

  // ---------------------------------------------------------------- visual
  private build(): THREE.Mesh {
    const g = this.group;
    const stone = new THREE.MeshStandardMaterial({ color: 0x1c1830, emissive: 0x2a1650, emissiveIntensity: 0.35, roughness: 0.7, metalness: 0.25, flatShading: true });
    const plate = new THREE.MeshStandardMaterial({ color: 0x2c2448, emissive: 0x5a2bb0, emissiveIntensity: 0.3, roughness: 0.45, metalness: 0.6, flatShading: true });
    const gold = new THREE.MeshStandardMaterial({ color: 0xffc14a, emissive: 0xffa21a, emissiveIntensity: 1.1, roughness: 0.3, flatShading: true });
    const voidM = new THREE.MeshStandardMaterial({ color: 0x050308, emissive: 0x000000, roughness: 1 });
    this.mats.push(stone, plate, gold);
    const add = (m: THREE.Mesh, x: number, y: number, z: number, parent: THREE.Object3D = g): THREE.Mesh => {
      m.position.set(x, y, z); m.castShadow = true; parent.add(m); return m;
    };
    // pernas e quadril
    for (const s of [-1, 1]) {
      add(new THREE.Mesh(new THREE.CylinderGeometry(0.75, 1.0, 3.4, 8), stone), s * 1.15, 1.7, 0);
      add(new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.6, 1.9), plate), s * 1.15, 0.3, 0.2); // pes
    }
    add(new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.0, 1.9), plate), 0, 3.6, 0);
    // tronco em V + placas
    add(new THREE.Mesh(new THREE.CylinderGeometry(2.3, 1.5, 3.4, 8), stone), 0, 5.7, 0);
    add(new THREE.Mesh(new THREE.BoxGeometry(2.6, 1.9, 0.5), plate), 0, 6.0, 1.15);
    const core = add(new THREE.Mesh(new THREE.OctahedronGeometry(0.55, 0), gold), 0, 6.1, 1.5);
    core.name = "core";
    // ombros
    for (const s of [-1, 1]) add(new THREE.Mesh(new THREE.DodecahedronGeometry(1.05, 0), plate), s * 2.45, 7.1, 0);
    // bracos (grupos pra animar o golpe): punho enorme
    for (const [arm, s] of [[this.armL, -1], [this.armR, 1]] as const) {
      arm.position.set(s * 2.55, 7.0, 0);
      g.add(arm);
      add(new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.65, 2.6, 7), stone), 0, -1.4, 0, arm);
      add(new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.55, 2.2, 7), stone), 0, -3.4, 0.2, arm);
      add(new THREE.Mesh(new THREE.DodecahedronGeometry(0.95, 0), plate), 0, -4.8, 0.3, arm);
      add(new THREE.Mesh(new THREE.OctahedronGeometry(0.28, 0), gold), 0, -4.8, 1.1, arm);
    }
    // cabeca = ECLIPSE: disco negro com coroa dourada atras
    add(new THREE.Mesh(new THREE.SphereGeometry(0.95, 16, 12), voidM), 0, 8.55, 0.2);
    const corona = add(new THREE.Mesh(new THREE.TorusGeometry(1.35, 0.22, 8, 36), gold), 0, 8.55, -0.05);
    corona.name = "corona";
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const ray = add(new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.9, 4), gold), Math.cos(a) * 1.95, 8.55 + Math.sin(a) * 1.95, -0.1);
      ray.rotation.z = a - Math.PI / 2;
    }
    // halo que gira em volta
    const halo = add(new THREE.Mesh(new THREE.TorusGeometry(5.2, 0.12, 6, 64), gold), 0, 4.2, 0);
    halo.rotation.x = Math.PI / 2;
    halo.name = "halo";
    // fragmentos orbitando
    for (let i = 0; i < 6; i++) {
      const sh = add(new THREE.Mesh(new THREE.OctahedronGeometry(0.4, 0), plate), 0, 0, 0);
      sh.scale.set(0.6, 1.4, 0.6);
      this.shards.push(sh);
    }
    // escudo (enquanto a raid nao comeca)
    const shieldMat = new THREE.MeshBasicMaterial({ color: 0xffd76a, transparent: true, opacity: 0.18, depthWrite: false, side: THREE.DoubleSide });
    const shield = new THREE.Mesh(new THREE.SphereGeometry(6.4, 24, 16), shieldMat);
    shield.position.y = 5;
    g.add(shield);
    return shield;
  }

  // ---------------------------------------------------------------- Boss / Damageable
  get position(): THREE.Vector3 { return this.group.position; }
  get isDead(): boolean { return this.dead; }
  get invulnerable(): boolean { return this.dormant || this.dead; }
  private get dormant(): boolean { return this.deps.serverNow() < this.startAt; }
  get hpFraction(): number { return Math.max(0, this.health.fraction); }
  get phaseNumber(): number { return this.hpFraction < this.cfg.phase2.hpBelow ? 2 : 1; }
  get nameKey(): string { return "boss.raidTitan"; }
  onHooked(): HookReaction { return "none"; } // gigante nao e puxado
  applyKnockback(): void { /* nao se mexe */ }
  onHit(): void { this.flash = 0.12; }
  debugSnapshot(): BossDebug { return { state: this.dormant ? "dormant" : "live", attack: this.pending[0]?.kind ?? "-", phase: this.phaseNumber, hp: Math.ceil(this.health.current) }; }

  /** Dano causado desde o ultimo pacote (a raid manda pro servidor). */
  takePending(): number { const p = this.health.pending; this.health.pending = 0; return p; }

  /** Vida GLOBAL vinda do servidor (1x/s). Mantem o que voce bateu e ainda nao foi enviado. */
  syncServer(hp: number, hpMax: number): void {
    if (this.dead) return;
    this.health.max = Math.max(1, hpMax);
    this.health.current = Math.max(1, Math.min(this.health.max, hp - this.health.pending));
  }

  /** O servidor encerrou: venceu (morre com animacao) ou acabou o tempo (some). */
  finish(win: boolean): void {
    this.health.ended = true;
    this.clearPending();
    if (win) { this.dead = true; this.health.current = 0; this.deathT = 0; }
    else this.removeRequested = true;
  }

  // ---------------------------------------------------------------- loop
  update(dt: number): void {
    this.t += dt;
    this.halo.rotation.z += dt * 0.4;
    this.corona.rotation.z -= dt * 0.25;
    for (let i = 0; i < this.shards.length; i++) {
      const a = this.t * 0.6 + (i / this.shards.length) * Math.PI * 2;
      this.shards[i].position.set(Math.cos(a) * 6.2, 5 + Math.sin(this.t * 1.3 + i) * 1.2, Math.sin(a) * 6.2);
      this.shards[i].rotation.y += dt * 2;
    }
    if (this.flash > 0) this.flash -= dt;
    for (const m of this.mats) m.emissiveIntensity = (m.color.getHex() === 0xffc14a ? 1.1 : 0.32) + (this.flash > 0 ? 0.9 : 0);

    if (this.dead) { this.updateDeath(dt); return; }

    const dormant = this.dormant;
    this.shield.visible = dormant;
    if (dormant) { (this.shield.material as THREE.MeshBasicMaterial).opacity = 0.14 + Math.sin(this.t * 3) * 0.05; return; }

    // olha pro jogador local (cada um ve o gigante encarando ele — ninguem mais depende disso)
    const p = this.deps.player.position;
    const face = Math.atan2(p.x - this.group.position.x, p.z - this.group.position.z);
    let d = face - this.group.rotation.y;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    this.group.rotation.y += d * Math.min(1, dt * 1.5);

    // 10.9GD — o Titã PERSEGUE o jogador (devagar, kiteável): só se move quando NÃO está preparando
    // um golpe (pra os telégrafos não "correrem"), mantém distância e nunca sai da arena.
    if (this.pending.length === 0) {
      const dx = p.x - this.group.position.x, dz = p.z - this.group.position.z;
      const dist = Math.hypot(dx, dz);
      const keep = this.cfg.radius + 2.5; // não cola no jogador
      if (dist > keep) {
        const step = Math.min(dist - keep, 3.8 * dt); // 10.9GE — mais rápido (~3,8 u/s; player anda ~5)
        let nx = this.group.position.x + (dx / dist) * step;
        let nz = this.group.position.z + (dz / dist) * step;
        const adx = nx - this.arena.x, adz = nz - this.arena.z, ad = Math.hypot(adx, adz);
        const lim = this.arena.r - this.cfg.radius - 1;
        if (ad > lim) { nx = this.arena.x + (adx / ad) * lim; nz = this.arena.z + (adz / ad) * lim; }
        this.group.position.x = nx; this.group.position.z = nz;
      }
    }

    this.schedule();
    this.resolve();

    // bracos: sobem enquanto um golpe esta preparando, descem no impacto
    const target = this.pending.length ? 1 : 0;
    this.raise += (target - this.raise) * Math.min(1, dt * (target ? 3 : 8));
    this.armL.rotation.x = -this.raise * 2.2;
    this.armR.rotation.x = -this.raise * 2.2;
  }

  /** Dispara o golpe do indice atual (uma vez por indice). */
  private schedule(): void {
    const now = this.deps.serverNow();
    const t = (now - this.startAt) / 1000;
    if (t < 0) return;
    const idx = Math.floor(t / this.cfg.period);
    if (idx === this.lastIdx) return;
    const into = t - idx * this.cfg.period;
    this.lastIdx = idx;
    const r = rand(this.seed ^ Math.imul(idx + 1, 2654435761));
    const kind = KINDS[Math.floor(r() * KINDS.length)];
    const p2 = this.phaseNumber === 2;
    const wm = p2 ? this.cfg.phase2.windupMult : 1;
    const c = this.cfg[kind];
    const windup = c.windup * wm;
    if (into > windup - 0.25) return; // entrou no meio: pula esse golpe (nao existe golpe "surpresa")
    const at = now + (windup - into) * 1000;
    const dur = (at - now) / 1000;
    const cx = this.group.position.x;
    const cz = this.group.position.z;
    const me = this.deps.player.position;
    const pts: Pending["pts"] = [];
    let donutMesh: THREE.Mesh | undefined;
    if (kind === "slam") {
      pts.push({ x: me.x, z: me.z, r: this.cfg.slam.radius });
      this.deps.telegraph.circle(me.x, me.z, this.cfg.slam.radius, dur, 0xffb03a);
    } else if (kind === "nova") {
      pts.push({ x: cx, z: cz, r: this.cfg.nova.radius });
      this.deps.telegraph.circle(cx, cz, this.cfg.nova.radius, dur, 0xffd76a);
    } else if (kind === "donut") {
      donutMesh = this.donut(cx, cz, this.cfg.donut.safe, this.arena.r);
    } else {
      const n = p2 ? this.cfg.meteors.countP2 : this.cfg.meteors.count;
      for (let i = 0; i < n; i++) {
        const a = r() * Math.PI * 2;
        const rr = this.cfg.radius + 3 + r() * (this.arena.r - this.cfg.radius - 5);
        pts.push({ x: cx + Math.cos(a) * rr, z: cz + Math.sin(a) * rr, r: this.cfg.meteors.radius });
      }
      pts.push({ x: me.x, z: me.z, r: this.cfg.meteors.radius }); // e um em cima de voce
      for (const q of pts) this.deps.telegraph.circle(q.x, q.z, q.r, dur, 0xff8a3a);
    }
    this.pending.push({ kind, at, pts, pct: c.pct, kb: c.knockback, donutMesh });
  }

  /** Anel do Eclipse: pinta o chao de fora (perigo) e deixa o circulo perto do Tita seguro. */
  private donut(cx: number, cz: number, inner: number, outer: number): THREE.Mesh {
    const mat = new THREE.MeshBasicMaterial({ color: 0xff5a3a, transparent: true, opacity: 0.18, depthWrite: false, side: THREE.DoubleSide });
    const m = new THREE.Mesh(new THREE.RingGeometry(inner, outer, 64, 1), mat);
    m.rotation.x = -Math.PI / 2;
    m.position.set(cx, 0.07, cz);
    this.scene.add(m);
    return m;
  }

  /** Impacto: cada celular confere se o SEU caçador estava na area. */
  private resolve(): void {
    const now = this.deps.serverNow();
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const a = this.pending[i];
      if (a.donutMesh) {
        const k = 1 - Math.max(0, (a.at - now) / 1000) / (this.cfg.donut.windup);
        (a.donutMesh.material as THREE.MeshBasicMaterial).opacity = 0.14 + Math.min(1, k) * 0.4;
      }
      if (now < a.at) continue;
      const pl = this.deps.player;
      const p = pl.position;
      let hit = false;
      let from = this.group.position;
      if (a.kind === "donut") {
        const d = Math.hypot(p.x - this.group.position.x, p.z - this.group.position.z);
        hit = d > this.cfg.donut.safe;
      } else {
        for (const q of a.pts) {
          if ((p.x - q.x) ** 2 + (p.z - q.z) ** 2 <= (q.r + 0.4) ** 2) { hit = true; from = _v.set(q.x, 0, q.z); break; }
        }
      }
      // efeito do impacto (todo mundo ve)
      if (a.kind === "donut") {
        this.deps.particles.burst(this.group.position.x, 1, this.group.position.z, 0xff5a3a, { count: 40, speed: 16, dur: 0.7 });
      } else {
        for (const q of a.pts) this.deps.particles.burst(q.x, 0.5, q.z, a.kind === "nova" ? 0xffd76a : 0xff8a3a, { count: a.kind === "nova" ? 36 : 12, speed: a.kind === "nova" ? 14 : 7, dur: 0.5 });
      }
      this.deps.impulse(a.kind === "slam" ? 0.12 : 0.2);
      if (hit && !pl.isDead) {
        dealDamage(pl, { baseDamage: pl.health.max * a.pct, critChance: 0, critDamageMult: 1, armor: 0 }, {
          targetKind: "player", attackerRank: "boss", damageType: "arcane", knockbackFrom: from.clone(), knockbackForce: a.kb,
        });
      }
      if (a.donutMesh) this.disposeDonut(a.donutMesh);
      this.pending.splice(i, 1);
    }
  }

  private disposeDonut(m: THREE.Mesh): void {
    this.scene.remove(m);
    m.geometry.dispose();
    (m.material as THREE.Material).dispose();
  }

  private clearPending(): void {
    for (const a of this.pending) if (a.donutMesh) this.disposeDonut(a.donutMesh);
    this.pending = [];
  }

  private updateDeath(dt: number): void {
    this.deathT += dt;
    // desaba: afunda, inclina e o eclipse "apaga"
    this.group.position.y = -this.deathT * 1.6;
    this.group.rotation.x = Math.min(0.5, this.deathT * 0.25);
    if (Math.random() < 0.5) this.deps.particles.burst(this.group.position.x + (Math.random() - 0.5) * 6, 4, this.group.position.z + (Math.random() - 0.5) * 6, 0xffc14a, { count: 8, speed: 9, dur: 0.6 });
    if (this.deathT > 4) this.removeRequested = true;
  }

  dispose(scene: THREE.Scene): void {
    this.clearPending();
    scene.remove(this.group);
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose()); else mat?.dispose();
    });
  }
}
